import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  useToast,
  Badge,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Alert,
  AlertIcon,
  Switch,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import {
  previewEscolaridadesImport,
  commitEscolaridadesImport,
  previewEscolaridadesFromDirectory,
  commitEscolaridadesFromDirectory,
} from 'services/academic.service';

export default function ImportEscolaridadesView() {
  const toast = useToast();
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const [files, setFiles] = useState([]);
  const [useDirectory, setUseDirectory] = useState(true);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const summary = useMemo(() => preview || null, [preview]);

  const onFileChange = (e) => {
    setFiles(Array.from(e.target.files || []));
    setPreview(null);
  };

  const runPreview = async () => {
    try {
      setLoading(true);
      let res;
      if (useDirectory) {
        res = await previewEscolaridadesFromDirectory();
      } else {
        if (!files.length) {
          toast({ title: 'Seleccioná al menos un archivo .xls', status: 'warning', duration: 3000 });
          return;
        }
        res = await previewEscolaridadesImport(files);
      }
      setPreview(res?.payload || null);
      toast({ title: 'Preview generado', status: 'success', duration: 2500 });
    } catch (err) {
      console.error(err);
      toast({
        title: 'Error en preview',
        description: err?.response?.data?.message || err.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const runCommit = async () => {
    if (!preview) {
      toast({ title: 'Primero generá un preview', status: 'warning', duration: 3000 });
      return;
    }
    const ok = window.confirm(
      `¿Confirmás importar ${preview.files || 0} escolaridades?\nSe crearán estudiantes/materias faltantes y el historial académico.`,
    );
    if (!ok) return;

    try {
      setLoading(true);
      let res;
      if (useDirectory) {
        res = await commitEscolaridadesFromDirectory();
      } else {
        res = await commitEscolaridadesImport(files);
      }
      setPreview(res?.payload || null);
      toast({ title: 'Importación completada', status: 'success', duration: 4000 });
    } catch (err) {
      console.error(err);
      toast({
        title: 'Error al importar',
        description: err?.response?.data?.message || err.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Card p="20px">
        <Flex justify="space-between" align="center" mb="20px" gap="12px" wrap="wrap">
          <Heading size="md">Importar escolaridades legacy</Heading>
          <HStack>
            <Button variant="outline" onClick={runPreview} isLoading={loading}>
              Preview
            </Button>
            <Button colorScheme="brand" onClick={runCommit} isLoading={loading} isDisabled={!preview}>
              Confirmar importación
            </Button>
          </HStack>
        </Flex>

        <VStack align="stretch" spacing="16px" mb="24px">
          <FormControl display="flex" alignItems="center">
            <FormLabel mb="0" mr="12px">
              Usar carpeta del servidor (`admin-back/escolaridades`)
            </FormLabel>
            <Switch
              isChecked={useDirectory}
              onChange={(e) => {
                setUseDirectory(e.target.checked);
                setPreview(null);
              }}
            />
          </FormControl>

          {!useDirectory && (
            <FormControl>
              <FormLabel>Archivos .xls</FormLabel>
              <Input type="file" accept=".xls,.xlsx" multiple onChange={onFileChange} p="4px" />
              <Text mt="8px" fontSize="sm" color="gray.500">
                {files.length ? `${files.length} archivo(s) seleccionado(s)` : 'Ningún archivo seleccionado'}
              </Text>
            </FormControl>
          )}

          <Alert status="info" borderRadius="md">
            <AlertIcon />
            El preview no escribe en la base. La confirmación crea estudiantes, planes, materias e historial
            faltantes de forma idempotente.
          </Alert>
        </VStack>

        {summary && (
          <>
            <SimpleGrid columns={{ base: 2, md: 4, xl: 6 }} spacing="12px" mb="20px">
              <StatCard label="Archivos" value={summary.files} />
              <StatCard label="Alumnos nuevos" value={summary.studentsToCreate} />
              <StatCard label="Alumnos existentes" value={summary.studentsExisting} />
              <StatCard label="Materias nuevas" value={summary.subjectsToCreate} />
              <StatCard label="Cursos" value={summary.courseGradesToCreate} />
              <StatCard label="Exámenes" value={summary.examGradesToCreate} />
              <StatCard label="Reválidas" value={summary.revalidationsToCreate} />
              <StatCard label="Duplicados omitidos" value={summary.skippedDuplicates} />
              <StatCard
                label="Estado"
                value={summary.committed ? 'COMMIT' : 'PREVIEW'}
                badge
              />
            </SimpleGrid>

            {!!summary.errors?.length && (
              <Alert status="error" mb="16px" borderRadius="md">
                <AlertIcon />
                {summary.errors.length} error(es). Revisá el detalle abajo.
              </Alert>
            )}

            <Box overflowX="auto" borderWidth="1px" borderColor={borderColor} borderRadius="md">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Documento</Th>
                    <Th>Alumno</Th>
                    <Th>Plan</Th>
                    <Th isNumeric>Intentos</Th>
                    <Th>Alta</Th>
                    <Th>Archivo</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {(summary.byStudent || []).map((row) => (
                    <Tr key={`${row.document}-${row.sourceName}`}>
                      <Td>{row.document}</Td>
                      <Td>{row.fullName}</Td>
                      <Td>{row.studyPlan}</Td>
                      <Td isNumeric>{row.attempts}</Td>
                      <Td>
                        <Badge colorScheme={row.willCreateStudent ? 'green' : 'gray'}>
                          {row.willCreateStudent ? 'Crear' : 'Existente'}
                        </Badge>
                      </Td>
                      <Td>{row.sourceName}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>

            {!!summary.warnings?.length && (
              <Box mt="16px">
                <Text fontWeight="600" mb="8px">
                  Warnings ({summary.warnings.length})
                </Text>
                <Box maxH="180px" overflowY="auto" fontSize="sm">
                  {summary.warnings.slice(0, 50).map((w, i) => (
                    <Text key={`${i}-${w}`}>{w}</Text>
                  ))}
                  {summary.warnings.length > 50 && (
                    <Text color="gray.500">… y {summary.warnings.length - 50} más</Text>
                  )}
                </Box>
              </Box>
            )}

            {!!summary.errors?.length && (
              <Box mt="16px">
                <Text fontWeight="600" mb="8px" color="red.500">
                  Errores ({summary.errors.length})
                </Text>
                <Box maxH="180px" overflowY="auto" fontSize="sm">
                  {summary.errors.map((w, i) => (
                    <Text key={`${i}-${w}`}>{w}</Text>
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}
      </Card>
    </Box>
  );
}

function StatCard({ label, value, badge }) {
  return (
    <Stat p="12px" borderWidth="1px" borderRadius="md">
      <StatLabel fontSize="xs">{label}</StatLabel>
      <StatNumber fontSize="lg">
        {badge ? <Badge colorScheme={value === 'COMMIT' ? 'green' : 'blue'}>{value}</Badge> : value}
      </StatNumber>
    </Stat>
  );
}
